import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import apiService from "../services/api";
import {
  isDashboardWebPlayerLaunch,
  limitDashboardPlaylistResults,
  prepareDashboardPlaylists,
  searchDashboardPlaylists,
} from "../utils/dashboardPlaylistPicker";
import webPlayerStorage from "../utils/webPlayerStorage";

const WebPlayerLogin = () => {
  const [fromDashboard] = useState(() =>
    isDashboardWebPlayerLaunch(window.location.search)
  );
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(null); // null | "lunakey" | "playlist" | "login"
  const [userPlaylists, setUserPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [formData, setFormData] = useState({
    url: "https://",
    username: "",
    password: "",
    lunaKey: "",
    playlistUrl: "https://",
    playlistName: "",
  });
  const [loadingPlaylists, setLoadingPlaylists] = useState(fromDashboard);
  const [playlistLoadError, setPlaylistLoadError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showSplash, setShowSplash] = useState(false);
  const [loginData, setLoginData] = useState(null);
  const [backdrop, setBackdrop] = useState(null);
  const [movieLogo, setMovieLogo] = useState(null);
  const [weather, setWeather] = useState(null);
  const [playlistDropdownOpen, setPlaylistDropdownOpen] = useState(false);
  const [playlistSearch, setPlaylistSearch] = useState("");
  const [linkOverlay, setLinkOverlay] = useState(null);
  const [canSkipSplash, setCanSkipSplash] = useState(false);
  const playlistDropdownRef = useRef(null);
  const splashDoneRef = useRef(false);
  const linkPollRef = useRef(null);
  const navigate = useNavigate();

  const loadDashboardPlaylists = useCallback(async () => {
    if (!fromDashboard) {
      setLoadingPlaylists(false);

      return;
    }

    setPlaylistLoadError("");
    setLoadingPlaylists(true);
    try {
      const { data: playlists } = await apiService.getPlaylists();

      setUserPlaylists(prepareDashboardPlaylists(playlists));
    } catch (error) {
      setUserPlaylists([]);
      setPlaylistLoadError(
        error.status === 401
          ? "Your dashboard session has expired. Return to the dashboard and sign in again."
          : "We couldn't load your dashboard playlists. You can retry or sign in manually below."
      );
    } finally {
      setLoadingPlaylists(false);
    }
  }, [fromDashboard]);

  useEffect(() => {
    const checkAuth = async () => {
      const token = webPlayerStorage.getToken();

      if (token && !fromDashboard) {
        try {
          navigate("/web-player/profiles");
        } catch (err) {
          webPlayerStorage.removeToken();
        }
      }
    };

    checkAuth();
  }, [fromDashboard, navigate]);

  useEffect(() => {
    loadDashboardPlaylists();
  }, [loadDashboardPlaylists]);

  // Fetch trending backdrop
  useEffect(() => {
    const fetchBackdrop = async () => {
      try {
        const response = await fetch(
          "https://api.lunova.app/v2/login/trending",
          {
            headers: {
              Authorization: "Bearer NoAuth",
            },
          }
        );
        const data = await response.json();

        if (data?.data) {
          setBackdrop(data.data.backdropPath);
          setMovieLogo(data.data.logoPath);
        }
      } catch (error) {
        console.error("Failed to fetch backdrop:", error);
      }
    };

    fetchBackdrop();
  }, []);

  // Fetch weather
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        // Get location from IP
        const ipResponse = await fetch("https://ipapi.co/json/");
        const ipData = await ipResponse.json();
        const { latitude, longitude } = ipData;

        if (latitude && longitude) {
          // Fetch weather from Open-Meteo
          const weatherResponse = await fetch(
            `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,weather_code&temperature_unit=fahrenheit`
          );
          const weatherData = await weatherResponse.json();

          if (weatherData?.current) {
            setWeather({
              temp: Math.round(weatherData.current.temperature_2m),
              code: weatherData.current.weather_code,
            });
          }
        }
      } catch (error) {
        console.error("Failed to fetch weather:", error);
      }
    };

    fetchWeather();
  }, []);

  // Close playlist dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        playlistDropdownRef.current &&
        !playlistDropdownRef.current.contains(event.target)
      ) {
        setPlaylistDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredPlaylists = useMemo(
    () => searchDashboardPlaylists(userPlaylists, playlistSearch),
    [playlistSearch, userPlaylists]
  );
  const visiblePlaylists = useMemo(
    () => limitDashboardPlaylistResults(filteredPlaylists),
    [filteredPlaylists]
  );

  // Get weather icon based on WMO code
  const getWeatherIcon = (code) => {
    if (code === 0) return "fa-sun"; // Clear sky
    if (code === 1 || code === 2) return "fa-cloud-sun"; // Partly cloudy
    if (code === 3) return "fa-cloud"; // Overcast
    if (code >= 45 && code <= 48) return "fa-smog"; // Fog
    if (code >= 51 && code <= 67) return "fa-cloud-rain"; // Drizzle/Rain
    if (code >= 71 && code <= 77) return "fa-snowflake"; // Snow
    if (code >= 80 && code <= 82) return "fa-cloud-showers-heavy"; // Rain showers
    if (code >= 85 && code <= 86) return "fa-snowflake"; // Snow showers
    if (code >= 95 && code <= 99) return "fa-cloud-bolt"; // Thunderstorm

    return "fa-cloud"; // Default
  };

  const proceedToProfiles = () => {
    if (splashDoneRef.current || !loginData) return;

    splashDoneRef.current = true;
    setShowSplash(false);
    webPlayerStorage.setLoginAuth(loginData);
    if (loginData.setup) {
      navigate("/web-player/setup");
    } else {
      navigate("/web-player/profiles");
    }
  };

  const proceedRef = useRef(proceedToProfiles);

  proceedRef.current = proceedToProfiles;

  useEffect(() => {
    if (!showSplash) return undefined;

    splashDoneRef.current = false;
    setCanSkipSplash(false);
    const skipTimer = setTimeout(() => setCanSkipSplash(true), 1000);
    const navTimer = setTimeout(() => proceedRef.current(), 3000);

    return () => {
      clearTimeout(skipTimer);
      clearTimeout(navTimer);
    };
  }, [showSplash]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (activeTab === "playlist") {
      if (!formData.playlistUrl || formData.playlistUrl === "https://") {
        toast.error("Please enter a valid M3U playlist URL");

        return;
      }

      setLoading(true);
      try {
        const response = await apiService.webPlayerRequest(
          "/v2/login/playlist",
          {
            method: "POST",
            body: JSON.stringify({
              playlist: formData.playlistUrl,
            }),
          }
        );

        if (!response.auth || response.status !== 200) {
          throw new Error("Invalid M3U playlist URL");
        }

        if (response.token) {
          webPlayerStorage.setToken(response.token);
        }

        setLoginData({
          playlist: {
            id: response.playlist.playlistId,
            name: response.playlist.playlistName,
          },
          token: response.token,
          profiles: response.profiles || [],
          setup: response.setup || false,
        });
        setShowSplash(true);
      } catch (error) {
        toast.error(error.message || "Failed to connect to M3U playlist");
      } finally {
        setLoading(false);
      }
    } else if (activeTab === "lunakey") {
      if (
        !formData.lunaKey ||
        formData.lunaKey.replace(/-/g, "").length !== 12
      ) {
        toast.error("Please enter a valid LunaKey");

        return;
      }

      setLoading(true);
      try {
        const response = await apiService.webPlayerLogin({
          code: formData.lunaKey,
        });

        if (!response.auth || response.status !== 200) {
          throw new Error("Invalid lunakey credentials");
        }

        if (response.token) {
          webPlayerStorage.setToken(response.token);
        }

        setLoginData({
          playlist: response.playlist
            ? {
                id: response.playlist.playlistId,
                name: response.playlist.playlistName,
              }
            : null,
          token: response.token,
          profiles: response.profiles || [],
          setup: response.setup || false,
        });
        setShowSplash(true);
      } catch (error) {
        toast.error(error.message || "LunaKey authentication failed");
      } finally {
        setLoading(false);
      }
    } else if (activeTab === "login") {
      if (
        !formData.playlistUrl ||
        formData.playlistUrl === "https://" ||
        !formData.username ||
        !formData.password
      ) {
        toast.error("Please fill in all fields");

        return;
      }

      setLoading(true);
      try {
        const response = await apiService.webPlayerLogin({
          url: formData.playlistUrl,
          username: formData.username,
          password: formData.password,
        });

        if (!response.auth || response.status !== 200) {
          throw new Error("Invalid credentials");
        }

        if (response.token) {
          webPlayerStorage.setToken(response.token);
        }

        setLoginData({
          playlist: response.playlist
            ? {
                id: response.playlist.playlistId,
                name: response.playlist.playlistName,
              }
            : null,
          token: response.token,
          profiles: response.profiles || [],
          setup: response.setup || false,
        });
        setShowSplash(true);
      } catch (error) {
        toast.error(error.message || "Login failed. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePlaylistSelect = async (playlistId) => {
    const playlist = userPlaylists.find((p) => p.id === playlistId);

    if (!playlist) return;

    setSelectedPlaylist(playlistId);
    setLoading(true);

    try {
      const response = await apiService.webPlayerLogin({
        code: playlist.code,
      });

      if (!response.auth || response.status !== 200) {
        throw new Error("Failed to connect to playlist");
      }

      if (response.token) {
        webPlayerStorage.setToken(response.token);
      }

      webPlayerStorage.setPlaylist(playlist);

      setLoginData({
        playlist: response.playlist
          ? {
              id: response.playlist.playlistId,
              name: response.playlist.playlistName,
            }
          : { id: playlist.id, name: playlist.name },
        token: response.token,
        profiles: response.profiles || [],
        setup: response.setup || false,
      });
      setShowSplash(true);
    } catch (error) {
      toast.error(error.message || "Failed to connect to playlist");
      setSelectedPlaylist(null);
    } finally {
      setLoading(false);
    }
  };

  const showSelector = () => {
    setActiveTab(null);
    setShowPassword(false);
    setFormData({
      url: "https://",
      username: "",
      password: "",
      lunaKey: "",
      playlistUrl: "https://",
      playlistName: "",
    });
  };

  const openSignUp = () => {
    window.open(
      "https://dashboard.lunova.app/register?plan=trial",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const closeSmartphone = () => {
    if (linkPollRef.current) {
      clearInterval(linkPollRef.current);
      linkPollRef.current = null;
    }
    setLinkOverlay(null);
  };

  const openSmartphone = async () => {
    if (linkPollRef.current) {
      clearInterval(linkPollRef.current);
      linkPollRef.current = null;
    }

    setLinkOverlay({ code: "----", qr: null, welcome: false });

    try {
      const response = await fetch(`${apiService.baseURL}/v1/link/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ device_name: "Lunova Web" }),
      });
      const data = await response.json();
      const deviceId = data.device_link_code_id;
      const { code } = data;

      if (!response.ok || !deviceId || !code) {
        throw new Error("Couldn't generate a code. Try again.");
      }

      setLinkOverlay({
        code,
        qr: `${apiService.baseURL}/v1/link/qr/${deviceId}`,
        welcome: false,
      });

      const expiresAt = data.expires_at
        ? Date.parse(data.expires_at)
        : Date.now() + 5 * 60 * 1000;
      const interval = Math.max(1000, data.poll_interval_ms || 2000);

      linkPollRef.current = setInterval(async () => {
        if (Date.now() >= expiresAt) {
          if (linkPollRef.current) {
            clearInterval(linkPollRef.current);
            linkPollRef.current = null;
          }
          setLinkOverlay(null);
          toast.error("That code expired. Generating a new one…");

          return;
        }

        try {
          const statusResponse = await fetch(
            `${apiService.baseURL}/v1/link/status/${deviceId}`
          );
          const status = await statusResponse.json();

          if (status.link_status === "ready" && status.token) {
            if (linkPollRef.current) {
              clearInterval(linkPollRef.current);
              linkPollRef.current = null;
            }
            webPlayerStorage.setToken(status.token);
            setLinkOverlay((current) =>
              current ? { ...current, welcome: true } : current
            );
            setLoginData({
              playlist: null,
              token: status.token,
              profiles: status.profiles || [],
              setup: status.setup || false,
            });
            setTimeout(() => setShowSplash(true), 700);
          } else if (
            status.status === "expired" ||
            status.status === "consumed"
          ) {
            if (linkPollRef.current) {
              clearInterval(linkPollRef.current);
              linkPollRef.current = null;
            }
            setLinkOverlay(null);
            toast.error("That code expired. Generating a new one…");
          }
        } catch {
          // The next poll retries a transient status failure.
        }
      }, interval);
    } catch (error) {
      if (linkPollRef.current) {
        clearInterval(linkPollRef.current);
        linkPollRef.current = null;
      }
      setLinkOverlay(null);
      toast.error(error.message || "Couldn't generate a code. Try again.");
    }
  };

  useEffect(
    () => () => {
      if (linkPollRef.current) {
        clearInterval(linkPollRef.current);
      }
    },
    []
  );

  const selectorButtonClass =
    "login-selector-btn login-outline group flex w-full items-center focus:outline-none";
  const focusedSelectorButtonClass =
    "login-selector-btn login-focused group flex w-full items-center focus:outline-none";
  const fieldClass =
    "h-[47px] w-full rounded-full border border-white/20 bg-[#1A1A2E]/80 pl-11 pr-4 text-[15px] text-white placeholder:text-[#888888] focus:outline-none";

  return (
    <div
      className="login-screen relative flex min-h-screen overflow-hidden"
      style={{
        background:
          "linear-gradient(45deg, #030305 0%, #0E0E18 50%, #1C1C2A 100%)",
      }}
    >
      <style>{`
        .login-screen {
          --u: min(2px, calc(100vw / 960));
        }
        .login-logo {
          position: absolute;
          left: calc(56 * var(--u));
          top: calc(48 * var(--u));
          width: calc(72 * var(--u));
          height: calc(72 * var(--u));
        }
        .login-marketing {
          position: absolute;
          left: calc(56 * var(--u));
          top: calc(120 * var(--u));
          bottom: 0;
          display: flex;
          width: calc(500 * var(--u));
          max-width: calc(50vw - 72 * var(--u));
          flex-direction: column;
        }
        .login-copy {
          max-width: 100%;
        }
        .login-headline {
          font-size: calc(22 * var(--u));
          line-height: 1.2;
          font-weight: 500;
        }
        .login-body {
          margin-top: calc(16 * var(--u));
          font-size: calc(15 * var(--u));
          line-height: 1.45;
          font-weight: 300;
          color: #ccffffff;
        }
        .login-signup {
          margin-top: calc(28 * var(--u));
          height: calc(52 * var(--u));
          min-width: calc(200 * var(--u));
          padding-left: calc(24 * var(--u));
          padding-right: calc(28 * var(--u));
          gap: calc(16 * var(--u));
          border-radius: calc(12 * var(--u));
          font-size: calc(14 * var(--u));
        }
        .login-signup i {
          font-size: calc(16 * var(--u));
          width: calc(20 * var(--u));
          text-align: center;
        }
        .login-trending {
          position: absolute;
          left: calc(20 * var(--u));
          bottom: calc(20 * var(--u));
          width: 25%;
          max-height: calc(80 * var(--u));
        }
        .login-welcome {
          margin-bottom: calc(20 * var(--u));
          width: 100%;
          text-align: center;
          font-size: calc(22 * var(--u));
          font-weight: 300;
        }
        .login-selector {
          width: calc(320 * var(--u));
          max-width: 100%;
          display: flex;
          flex-direction: column;
          gap: calc(10 * var(--u));
        }
        .login-selector-btn {
          height: calc(52 * var(--u));
          border-radius: calc(12 * var(--u));
          padding-left: calc(20 * var(--u));
          padding-right: calc(20 * var(--u));
          font-size: calc(14 * var(--u));
          gap: calc(16 * var(--u));
        }
        .login-selector-btn i {
          width: calc(20 * var(--u));
          font-size: calc(16 * var(--u));
          text-align: center;
        }
        .login-outline {
          border: 1px solid rgba(255, 255, 255, 0.5);
          background: rgba(255, 255, 255, 0.1);
          color: #fff;
        }
        .login-outline:hover {
          border-color: transparent;
          background: #e0e0e0;
          color: #1a1a1a;
        }
        .login-focused {
          border: 1px solid transparent;
          background: #e0e0e0;
          color: #1a1a1a;
        }
        .login-badge {
          margin-left: auto;
          border-radius: calc(4 * var(--u));
          background: #ffd54f;
          padding: calc(2 * var(--u)) calc(6 * var(--u));
          font-size: calc(9 * var(--u));
          font-weight: 700;
          letter-spacing: 0.08em;
          color: #1a1a1a;
        }
        .login-footer {
          position: absolute;
          bottom: calc(20 * var(--u));
          left: 50%;
          z-index: 20;
          width: calc(400 * var(--u));
          max-width: calc(100% - 80px);
          transform: translateX(-50%);
          text-align: center;
          font-size: calc(9 * var(--u));
          font-weight: 300;
          line-height: 1.35;
          color: rgba(255, 255, 255, 0.52);
          pointer-events: none;
        }
        .login-weather {
          position: absolute;
          top: calc(24 * var(--u));
          right: calc(24 * var(--u));
          z-index: 20;
          display: flex;
          align-items: center;
          gap: calc(8 * var(--u));
          color: #fff;
          font-size: calc(18 * var(--u));
          font-weight: 400;
        }
        .login-weather i {
          font-size: calc(28 * var(--u));
        }
        .login-splash-logo {
          width: calc(100 * var(--u));
          height: calc(100 * var(--u));
        }
        .login-splash-skip {
          position: absolute;
          top: calc(24 * var(--u));
          right: calc(24 * var(--u));
          border-radius: calc(20 * var(--u));
          background: #fff;
          padding: calc(6 * var(--u)) calc(14 * var(--u));
          font-size: calc(11 * var(--u));
          font-weight: 700;
          color: #000;
        }
      `}</style>
      <div className="absolute inset-0 z-0">
        {backdrop ? (
          <motion.img
            alt="Backdrop"
            animate={{ opacity: 0.3 }}
            className="h-full w-full object-cover"
            initial={{ opacity: 0 }}
            src={backdrop}
            transition={{ duration: 1 }}
          />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/20 to-transparent" />
      </div>

      {/* Weather Widget - Top Right */}
      {weather && (
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="login-weather"
          initial={{ opacity: 0, y: -20 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <i className={`fas ${getWeatherIcon(weather.code)}`} />
          <span>{weather.temp}°F</span>
        </motion.div>
      )}

      {/* Dashboard Button - Bottom Right */}
      <motion.button
        animate={{ opacity: 0.4, y: 0 }}
        className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-20 text-white text-xs sm:text-sm font-medium bg-black/50 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg opacity-40 hover:opacity-100 hover:scale-105 transition-all duration-300"
        initial={{ opacity: 0, y: 20 }}
        transition={{ delay: 0.5, duration: 0.3 }}
        whileHover={{ opacity: 1, scale: 1.05 }}
        onClick={() => {
          webPlayerStorage.clearAll();
          window.location.href = "https://dashboard.lunova.app";
        }}
      >
        Dashboard
      </motion.button>

      <div className="relative z-10 hidden h-screen w-1/2 min-[600px]:block">
        <img
          alt="Lunova"
          className="login-logo object-contain"
          src="https://lunova.app/img/logo.png"
        />
        <div className="login-marketing">
          <div className="min-h-0 flex-[0.18]" />
          <div className="login-copy">
            <h1 className="login-headline text-white">
              Everything You Need in One Powerful Media Player
            </h1>
            <p className="login-body">
              Lunova Player is a fast, feature-rich media player built for every
              screen — bring your own content and enjoy a polished guide and
              enterprise-grade playback.
            </p>
            <button
              className="login-signup flex items-center bg-[#7C3AED] text-white transition hover:bg-[#E0E0E0] hover:text-[#1A1A1A]"
              type="button"
              onClick={openSignUp}
            >
              <i className="fa-solid fa-ticket" />
              One Day FREE Pass
            </button>
          </div>
          <div className="min-h-0 flex-[0.82]" />
        </div>
        {movieLogo && (
          <img
            alt=""
            className="login-trending h-auto object-contain object-left"
            src={movieLogo}
          />
        )}
      </div>

      <div className="relative z-10 flex h-screen w-full items-center justify-center overflow-y-auto px-12 py-12 min-[600px]:w-1/2 min-[960px]:px-10 min-[960px]:pb-5">
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="flex w-full flex-col items-center"
          initial={{ opacity: 0, y: 12 }}
          transition={{ duration: 0.4 }}
        >
          <img
            alt="Lunova"
            className="mx-auto mb-6 h-16 w-16 object-contain min-[600px]:hidden"
            src="https://lunova.app/img/logo.png"
          />
          {/* Saved playlists - only shown when coming from the dashboard */}
          {fromDashboard && (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 landscape:mb-2 sm:mb-6"
              initial={{ opacity: 0, y: -10 }}
              ref={playlistDropdownRef}
              transition={{ duration: 0.3 }}
            >
              {loadingPlaylists ? (
                <div
                  className="w-full px-4 py-3 sm:py-4 bg-gray-900/60 backdrop-blur-md border border-gray-600/50 rounded-2xl text-gray-300 text-sm text-center"
                  role="status"
                >
                  <i className="fas fa-spinner fa-spin mr-2" />
                  Loading your dashboard playlists...
                </div>
              ) : playlistLoadError ? (
                <div
                  className="w-full px-4 py-3 bg-red-950/70 backdrop-blur-md border border-red-500/40 rounded-2xl text-sm text-red-100"
                  role="alert"
                >
                  <p>{playlistLoadError}</p>
                  <div className="flex flex-wrap gap-3 mt-3">
                    <button
                      className="text-white font-medium hover:underline"
                      type="button"
                      onClick={loadDashboardPlaylists}
                    >
                      Retry
                    </button>
                    <a
                      className="text-white font-medium hover:underline"
                      href="https://dashboard.lunova.app"
                    >
                      Return to Dashboard
                    </a>
                  </div>
                </div>
              ) : userPlaylists.length > 0 ? (
                <div className="relative">
                  <button
                    aria-expanded={playlistDropdownOpen}
                    aria-haspopup="listbox"
                    className="w-full pl-10 sm:pl-14 pr-10 sm:pr-12 py-2.5 landscape:py-2 sm:py-4 bg-gray-900/60 backdrop-blur-md border border-purple-500/50 rounded-full text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm landscape:text-sm sm:text-base cursor-pointer hover:bg-gray-800/60 text-left"
                    disabled={loading}
                    type="button"
                    onClick={() =>
                      setPlaylistDropdownOpen(!playlistDropdownOpen)
                    }
                  >
                    <i className="fas fa-bookmark absolute left-4 sm:left-5 top-1/2 -translate-y-1/2 text-purple-400 text-sm" />
                    <span className="text-gray-300">
                      Select Saved Playlist ({userPlaylists.length})
                    </span>
                    <i
                      className={`fas fa-chevron-down absolute right-4 sm:right-5 top-1/2 -translate-y-1/2 text-purple-400 text-sm transition-transform duration-200 ${playlistDropdownOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  <AnimatePresence>
                    {playlistDropdownOpen && (
                      <motion.div
                        animate={{ opacity: 1, y: 0 }}
                        className="absolute top-full left-0 right-0 mt-2 bg-gray-900/95 backdrop-blur-md border border-gray-600/50 rounded-2xl overflow-hidden z-50 shadow-xl"
                        exit={{ opacity: 0, y: -10 }}
                        initial={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="p-3 border-b border-gray-700/50">
                          <div className="relative">
                            <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                            <input
                              autoFocus
                              aria-label="Search saved playlists"
                              className="w-full pl-9 pr-9 py-2 bg-gray-800/60 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                              placeholder="Search name, customer, domain, or username..."
                              type="search"
                              value={playlistSearch}
                              onChange={(e) =>
                                setPlaylistSearch(e.target.value)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Escape") {
                                  setPlaylistDropdownOpen(false);
                                  setPlaylistSearch("");
                                }
                              }}
                            />
                            {playlistSearch && (
                              <button
                                aria-label="Clear playlist search"
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                                type="button"
                                onClick={() => setPlaylistSearch("")}
                              >
                                <i className="fas fa-times text-sm" />
                              </button>
                            )}
                          </div>
                        </div>

                        <div
                          className="max-h-64 overflow-y-auto"
                          role="listbox"
                        >
                          {visiblePlaylists.length > 0 ? (
                            visiblePlaylists.map((playlist) => (
                              <button
                                aria-selected={selectedPlaylist === playlist.id}
                                className="w-full px-4 py-3 text-left text-white hover:bg-purple-600/30 disabled:opacity-60 transition-colors flex items-center gap-3 border-b border-gray-700/30 last:border-b-0"
                                disabled={loading}
                                key={playlist.id}
                                role="option"
                                type="button"
                                onClick={() => {
                                  setPlaylistDropdownOpen(false);
                                  setPlaylistSearch("");
                                  handlePlaylistSelect(playlist.id);
                                }}
                              >
                                <i
                                  className={`fas ${loading && selectedPlaylist === playlist.id ? "fa-spinner fa-spin" : "fa-play-circle"} text-purple-400`}
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">
                                    {playlist.name}
                                  </p>
                                  <p className="text-xs text-gray-400 truncate">
                                    {[playlist.domain, playlist.username]
                                      .filter(Boolean)
                                      .join(" • ")}
                                  </p>
                                </div>
                              </button>
                            ))
                          ) : (
                            <div className="px-4 py-6 text-center text-gray-400 text-sm">
                              <i className="fas fa-search mb-2 text-lg" />
                              <p>No playlists match your search.</p>
                            </div>
                          )}
                        </div>
                        {filteredPlaylists.length > visiblePlaylists.length && (
                          <p className="px-4 py-2 border-t border-gray-700/50 text-xs text-gray-400 text-center">
                            Showing the first {visiblePlaylists.length} of{" "}
                            {filteredPlaylists.length}. Keep typing to narrow
                            the results.
                          </p>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <div
                  className="w-full px-4 py-3 bg-gray-900/60 backdrop-blur-md border border-gray-600/50 rounded-2xl text-gray-300 text-sm text-center"
                  role="status"
                >
                  No active dashboard playlists are available for automatic
                  login.
                </div>
              )}
            </motion.div>
          )}

          {/* Divider */}
          {fromDashboard && userPlaylists.length > 0 && (
            <div className="flex items-center gap-4 mb-4 landscape:mb-2 sm:mb-6">
              <div className="flex-1 h-px bg-gray-600/50" />
              <span className="text-gray-400 text-xs sm:text-sm">
                or login manually
              </span>
              <div className="flex-1 h-px bg-gray-600/50" />
            </div>
          )}

          {activeTab === null ? (
            <div className="flex w-full flex-col items-center">
              <h2 className="login-welcome text-white">Welcome, Sign In</h2>
              <div className="login-selector">
                <button
                  className={focusedSelectorButtonClass}
                  type="button"
                  onClick={() => setActiveTab("lunakey")}
                >
                  <i className="fa-solid fa-key" />
                  <span>LunaKey™</span>
                  <span className="login-badge">PREMIUM</span>
                </button>
                <button
                  className={selectorButtonClass}
                  type="button"
                  onClick={() => setActiveTab("playlist")}
                >
                  <i className="fa-solid fa-list" />
                  <span>Playlist</span>
                </button>
                <button
                  className={selectorButtonClass}
                  type="button"
                  onClick={openSmartphone}
                >
                  <i className="fa-solid fa-qrcode" />
                  <span>Use Smartphone</span>
                </button>
                <button
                  className={selectorButtonClass}
                  type="button"
                  onClick={() => setActiveTab("login")}
                >
                  <i className="fa-solid fa-right-to-bracket" />
                  <span>Login Here</span>
                </button>
                <button
                  className="flex h-[52px] w-full items-center rounded-xl bg-[#7C3AED] px-5 text-sm text-white transition hover:bg-[#E0E0E0] hover:text-[#1A1A1A] min-[600px]:hidden"
                  type="button"
                  onClick={openSignUp}
                >
                  <i className="fa-solid fa-ticket w-5 text-center text-base" />
                  <span className="ml-4">One Day FREE Pass</span>
                </button>
              </div>
            </div>
          ) : (
            <form
              className="flex w-full max-w-[480px] flex-col gap-3"
              onSubmit={handleSubmit}
            >
              <div className="mb-1 flex items-center gap-3">
                <div className="flex h-11 flex-1 items-center justify-center rounded-full bg-white/10 text-sm text-white/70">
                  {activeTab === "lunakey"
                    ? "LunaKey™"
                    : activeTab === "playlist"
                      ? "Playlist"
                      : "Login Here"}
                </div>
                <button
                  className="h-11 shrink-0 rounded-full border border-white/50 bg-white/10 px-4 text-sm text-white transition hover:border-transparent hover:bg-[#E0E0E0] hover:text-[#1A1A1A]"
                  type="button"
                  onClick={showSelector}
                >
                  ❮ Back
                </button>
              </div>

              {activeTab === "lunakey" && (
                <>
                  <button
                    className="text-left text-sm text-[#AAAAAA] hover:text-white"
                    type="button"
                    onClick={openSignUp}
                  >
                    Don&apos;t have a LunaKey™? Get one here
                  </button>
                  <div className="relative">
                    <i className="fa-solid fa-key absolute left-4 top-1/2 -translate-y-1/2 text-[#888888]" />
                    <input
                      autoFocus
                      className={`${fieldClass} text-center font-mono tracking-wider`}
                      disabled={loading}
                      maxLength={14}
                      placeholder="XXXX-XXXX-XXXX"
                      type="text"
                      value={formData.lunaKey || ""}
                      onChange={(e) => {
                        const value = e.target.value
                          .toUpperCase()
                          .replace(/[^A-Z0-9]/g, "");
                        let formatted = "";

                        for (let i = 0; i < value.length && i < 12; i++) {
                          if (i > 0 && i % 4 === 0) {
                            formatted += "-";
                          }
                          formatted += value[i];
                        }
                        setFormData((prev) => ({
                          ...prev,
                          lunaKey: formatted,
                        }));
                      }}
                    />
                  </div>
                </>
              )}

              {activeTab === "playlist" && (
                <div className="relative">
                  <i className="fa-solid fa-globe absolute left-4 top-1/2 -translate-y-1/2 text-[#888888]" />
                  <input
                    autoFocus
                    className={fieldClass}
                    disabled={loading}
                    name="playlistUrl"
                    placeholder="https://"
                    type="text"
                    value={formData.playlistUrl}
                    onChange={handleInputChange}
                  />
                </div>
              )}

              {activeTab === "login" && (
                <>
                  <div className="relative">
                    <i className="fa-solid fa-list absolute left-4 top-1/2 -translate-y-1/2 text-[#888888]" />
                    <input
                      className={fieldClass}
                      disabled={loading}
                      name="playlistName"
                      placeholder="Playlist Name (Optional)"
                      type="text"
                      value={formData.playlistName}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="relative">
                    <i className="fa-solid fa-globe absolute left-4 top-1/2 -translate-y-1/2 text-[#888888]" />
                    <input
                      autoFocus
                      className={fieldClass}
                      disabled={loading}
                      name="playlistUrl"
                      placeholder="https://"
                      type="text"
                      value={formData.playlistUrl}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="relative">
                    <i className="fa-solid fa-user absolute left-4 top-1/2 -translate-y-1/2 text-[#888888]" />
                    <input
                      className={fieldClass}
                      disabled={loading}
                      name="username"
                      placeholder="Username"
                      type="text"
                      value={formData.username}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="relative">
                    <i className="fa-solid fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-[#888888]" />
                    <input
                      className={`${fieldClass} pr-12`}
                      disabled={loading}
                      name="password"
                      placeholder="Password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleInputChange}
                    />
                    <button
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#888888] hover:text-white"
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <i
                        className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
                      />
                    </button>
                  </div>
                </>
              )}

              <button
                className="mt-2 h-[47px] w-full rounded-full bg-[#903fe5] text-base font-medium text-white transition hover:bg-[#7C3AED] disabled:cursor-not-allowed disabled:opacity-50"
                disabled={loading}
                type="submit"
              >
                {loading ? "Logging in..." : "Login"}
              </button>
            </form>
          )}
        </motion.div>
      </div>

      <p className="login-footer">
        Lunova Player is the ultimate way to enjoy your favorite content, fast,
        feature-rich, and built for every screen. Lunova Player does not provide
        any type of media content.
      </p>

      {linkOverlay && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/70 px-6">
          <div className="flex w-full max-w-sm flex-col items-center text-center text-white">
            {linkOverlay.welcome ? (
              <>
                <h2 className="text-2xl font-light">Welcome!</h2>
                <p className="mt-2 text-sm text-white/70">
                  Getting your profiles ready…
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-white/80">Scan or visit</p>
                <p className="mt-1 text-base">lunova.app/link</p>
                {linkOverlay.qr ? (
                  <img
                    alt="Pairing code"
                    className="mt-6 h-[220px] w-[220px] rounded-2xl bg-white object-contain p-3"
                    src={linkOverlay.qr}
                  />
                ) : (
                  <div className="mt-6 flex h-[220px] w-[220px] items-center justify-center rounded-2xl bg-white/10">
                    <i className="fa-solid fa-spinner fa-spin text-2xl" />
                  </div>
                )}
                <p className="mt-6 text-sm text-white/70">
                  Then enter this code:
                </p>
                <p className="mt-2 text-4xl font-light tracking-[0.3em]">
                  {linkOverlay.code}
                </p>
              </>
            )}
            <button
              className="mt-8 h-11 rounded-xl border border-white/50 bg-white/10 px-8 text-sm text-white transition hover:bg-[#E0E0E0] hover:text-[#1A1A1A]"
              type="button"
              onClick={closeSmartphone}
            >
              ❮ Back
            </button>
          </div>
        </div>
      )}

      <AnimatePresence>
        {showSplash && (
          <motion.div
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            style={{
              background:
                "linear-gradient(to top right, #030305 0%, #0E0E18 50%, #1C1C2A 100%)",
            }}
            onClick={() => {
              if (canSkipSplash) proceedToProfiles();
            }}
          >
            <img
              alt="Lunova"
              className="login-splash-logo object-contain"
              src="https://lunova.app/img/logo.png"
            />
            {canSkipSplash && (
              <button
                className="login-splash-skip"
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  proceedToProfiles();
                }}
              >
                Skip
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WebPlayerLogin;
